import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrency, toMoneyString } from '@/lib/i18n';
import type { CurrencyInfo } from '@/lib/shared-types';
import { useAuth } from '@/Hooks/useAuth';
import { useAppSelector } from '@/Redux/store';
import {
  ensureBranchCurrency,
  ensureCurrencyContext,
  currencyInfoFor,
  getCurrencyContext,
  refreshCurrencyContext,
  resolveBranchCurrency,
  subscribeCurrencyContext,
  tenantCurrencyCode,
  type CurrencySource,
} from '@/lib/currency';

export interface UseCurrencyResult {
  /** ISO 4217 code for the amounts on this screen. Never empty. */
  code: string;
  /**
   * Descriptor for `code` — name, symbol, minor unit, rounding, placement.
   * Never null: an unknown or not-yet-loaded code is synthesised from the ISO
   * minor-unit table, so `info.decimals` is always a usable answer.
   */
  info: CurrencyInfo;
  /**
   * Formats an amount for display. Pass the API's decimal STRING straight
   * through — never `Number(...)` it first; that is how a total on screen ends
   * up disagreeing with the total on the bill.
   */
  format: (amount: string | number | null | undefined) => string;
  /** The decimal string to send back to the API for a user-entered amount. */
  toMoney: (amount: string | number) => string;
  /** `info.decimals`, hoisted because input `step`/validation needs it a lot. */
  decimals: number;
  /** Whether `code` came from the branch, inherited from the tenant, or is the
   *  platform fallback because neither could be read. */
  source: CurrencySource;
  /** The tenant's base currency code — what an inheriting branch resolves to.
   *  This is what makes "Inherited from organization (SAR)" printable. */
  tenantCode: string;
  /** Active currencies, for a picker. Empty until the first load lands. */
  currencies: CurrencyInfo[];
  /** False while the one-time reference-data fetch is still in flight. */
  ready: boolean;
  /** Re-reads the reference data. Call after Settings writes a currency. */
  reload: () => Promise<void>;
}

/**
 * The currency every dashboard screen should format money with.
 *
 * ```tsx
 * const { format } = useCurrency();
 * <span>{format(check.total)}</span>          // check.total is a string — good
 * ```
 *
 * Resolution, in order: the branch's own currency → the tenant's base currency
 * → `SAR`. A branch whose `currencyId` is null genuinely inherits; that is a
 * state to render ("Inherited from tenant (SAR)"), not a blank.
 *
 * Reference data is fetched once per page load and cached in module scope
 * (`@/lib/currency`), so calling this hook in fifty components costs one
 * request. Every consumer repaints together when Settings changes a currency.
 *
 * @param branchId Resolve against this branch instead of the active one. Pass
 *   it when a row belongs to a branch other than the one selected in the header
 *   — a check, an order, a report line. Omit it for the current branch.
 */
export function useCurrency(branchId?: string | null): UseCurrencyResult {
  const { i18n } = useTranslation();
  const { user } = useAuth();
  const activeBranchId = useAppSelector((state) => state.ui.activeBranchId);
  const tenantId = user?.tenantId ?? null;

  const context = useSyncExternalStore(subscribeCurrencyContext, getCurrencyContext);
  const effectiveBranchId = branchId === undefined ? activeBranchId : branchId;

  useEffect(() => {
    void ensureCurrencyContext(tenantId);
  }, [tenantId]);

  // The per-branch call carries no permission key, so it is the one link in the
  // chain that answers for a waiter or cashier who cannot list branches.
  useEffect(() => {
    if (effectiveBranchId) void ensureBranchCurrency(effectiveBranchId);
  }, [effectiveBranchId]);

  const { code, source } = useMemo(
    () => resolveBranchCurrency(effectiveBranchId, context),
    [effectiveBranchId, context],
  );
  const info = useMemo(() => currencyInfoFor(code, context), [code, context]);
  const tenantCode = useMemo(() => tenantCurrencyCode(context).code, [context]);

  const language = i18n.language;
  const format = useCallback(
    (amount: string | number | null | undefined) =>
      formatCurrency(amount ?? 0, code, language),
    [code, language],
  );
  const toMoney = useCallback((amount: string | number) => toMoneyString(amount, code), [code]);
  const reload = useCallback(async () => {
    await refreshCurrencyContext(tenantId);
  }, [tenantId]);

  return {
    code,
    info,
    format,
    toMoney,
    decimals: info.decimals,
    source,
    tenantCode,
    currencies: context.currencies,
    ready: context.ready,
    reload,
  };
}

export default useCurrency;
